import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../../HandleAPICalls/actions";
import { toastr } from "react-redux-toastr";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import VmidDashboardDetailsComponent from "./component";

const VmidDashboardDetailsContainer = (props) => {
  const navigate = useNavigate();
  const { uid } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uidDetails, setUidDetails] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [vmList, setVmList] = useState([]);
  const [uidList, setUidList] = useState([]);
  const [selectedVmDetails, setSelectedVmDetails] = useState(null);
  const [mapSuccess, setMapSuccess] = useState(false);
  const [unmapSuccess, setUnmapSuccess] = useState(false);
  const [mapConfirmationOpen, setMapConfirmationOpen] = useState(false);
  const [pendingMapData, setPendingMapData] = useState(null);
  const [initialVmInfo, setInitialVmInfo] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [initialUidLoaded, setInitialUidLoaded] = useState(false);

  const getDashboardCurrentMode = () => {
    if (location.pathname.includes('/uid-unmap/')) {
      return 'unmap';
    } else if (location.pathname.includes('/uid-map/')) {
      return 'map';
    } else {
      return 'details';
    }
  };

  const currentMode = getDashboardCurrentMode();

  useEffect(() => {
    if (uid && !initialUidLoaded) {
      loadDashboardUidDetails(uid);
      setInitialUidLoaded(true);
    }
    handleDashboardGetVmList();
    handleDashboardGetUidList(""); // Load initial UID list
  }, [uid]);

  // Initial UID details API
  const loadDashboardUidDetails = (uid) => {
    setLoading(true);
    setError(null);
   
    props.getDataFromAPI(
      `/partner/api/uid/${uid}/details`,
      "GET",
      undefined,
      (response) => {
        console.log("Initial UID Details API Response:", response);
        
        // Handle the response format for initial API
        const uidData = response;
        setUidDetails(uidData);
        
        // Set initial VM info if available and not empty
        if (uidData?.vmInfo && Object.keys(uidData.vmInfo).length > 0 && uidData.vmInfo.id) {
          console.log("Setting initial VM info:", uidData.vmInfo);
          setInitialVmInfo(uidData.vmInfo);
        } else {
          console.log("No initial VM info found");
          setInitialVmInfo(null);
        }
        
        setLoading(false);
      },
      (error) => {
        const errorMessage = error?.message || "Failed to fetch UID details";
        setError(errorMessage);
        toastr.error("Error", errorMessage);
        setLoading(false);
      }
    );
  };

  // Selected UID details API
  const loadSelectedUidDetails = (selectedUid) => {
    setLoading(true);
    setError(null);
   
    props.getDataFromAPI(
      `/partner/api/v2/uids/${selectedUid}`,
      "GET",
      undefined,
      (response) => {
        console.log("Selected UID Details API Response:", response);
        
        // Handle the new API response format with data wrapper
        const uidData = response?.data || response;
        setUidDetails(uidData);
        
        // IMPORTANT: Do NOT update initial VM info when user selects new UID
        // Keep the original initial VM info (if any)
        console.log("Keeping original initial VM info:", initialVmInfo);
        
        setLoading(false);
      },
      (error) => {
        const errorMessage = error?.message || "Failed to fetch selected UID details";
        setError(errorMessage);
        toastr.error("Error", errorMessage);
        setLoading(false);
      }
    );
  };

  // Get UID List with search functionality
  const handleDashboardGetUidList = async (searchQuery = "") => {
    setSearchLoading(true);
    
    const apiUrl = searchQuery 
      ? `/partner/api/v2/uid/listuids?search=${encodeURIComponent(searchQuery)}`
      : `/partner/api/v2/uid/listuids`;

    console.log("Fetching UID list from:", apiUrl);

    props.getDataFromAPI(
      apiUrl,
      "GET",
      undefined,
      (response) => {
        console.log("UID List API Response:", response);
        const uids = Array.isArray(response) ? response : 
                    response?.items || response?.data || response?.uids || [];
        console.log("Processed UID List:", uids);
        setUidList(uids);
        setSearchLoading(false);
      },
      (err) => {
        const errorMessage = err?.message || "Unable to fetch UID list";
        console.error("Unable to fetch UID list:", errorMessage);
        toastr.error("Error", errorMessage);
        setUidList([]);
        setSearchLoading(false);
      }
    );
  };

  // Handle UID search
  const handleDashboardUidSearch = (query) => {
    setSearchQuery(query);
    const timeoutId = setTimeout(() => {
      handleDashboardGetUidList(query);
    }, 300);
    
    return () => clearTimeout(timeoutId);
  };

  const handleDashboardUidSelect = (selectedUid) => {
    console.log("UID selected in container:", selectedUid);
    console.log("Current initial VM info:", initialVmInfo);
    
    if (selectedUid) {
      loadSelectedUidDetails(selectedUid);
    } else {
      setUidDetails(null);
      // Don't reset initial VM info when UID changes
    }
  };

  const fetchDashboardVmDetails = (id) => {
    setSearchLoading(true);
    
    props.getDataFromAPI(
      `/partner/api/v2/machine/${id}`,
      "GET",
      undefined,
      (response) => {
        setSelectedVmDetails(response);
        setSearchLoading(false);
      },
      (error) => {
        const errorMessage = error?.message || "Failed to fetch VM details";
        toastr.error("Error", errorMessage);
        setSearchLoading(false);
      }
    );
  };

  const handleDashboardVmSelect = (vmId) => {
    if (vmId) {
      fetchDashboardVmDetails(vmId);
    } else {
      setSelectedVmDetails(null);
    }
  };

  // Get VM List with search functionality
  const handleDashboardGetVmList = async (searchQuery = "") => {
    setSearchLoading(true);
    
    const apiUrl = searchQuery 
      ? `/partner/api/v2/machine/unmapped?search=${encodeURIComponent(searchQuery)}`
      : `/partner/api/v2/machine/unmapped`;

    console.log("Fetching VM list from:", apiUrl);

    props.getDataFromAPI(
      apiUrl,
      "GET",
      undefined,
      (response) => {
        console.log("VM List API Response:", response);
        const vms = Array.isArray(response) ? response : 
                   response?.data || response?.machines || response?.items || [];
        setVmList(vms);
        setSearchLoading(false);
      },
      (err) => {
        const errorMessage = err?.message || "Unable to fetch VM list";
        console.error("Unable to fetch VM list:", errorMessage);
        toastr.error("Error", errorMessage);
        setVmList([]);
        setSearchLoading(false);
      }
    );
  };

  // Handle VM search
  const handleDashboardVmSearch = (query) => {
    const timeoutId = setTimeout(() => {
      handleDashboardGetVmList(query);
    }, 300);
    
    return () => clearTimeout(timeoutId);
  };

  const handleDashboardUnmap = (reason) => {
    if (!uidDetails) return;
    
    setLoading(true);
    setError(null);
    
    const currentUid = uidDetails?.uidInfo?.uid || uidDetails?.data?.uidInfo?.uid || uid;
    const currentVmId = initialVmInfo?.id || uidDetails?.vmInfo?.id || 0;
    
    const unmapPayload = {
      uids: [currentUid],
      vmId: currentVmId,
      reason: reason
    };

    return new Promise((resolve, reject) => {
      props.getDataFromAPI(
        `/partner/api/v2/uid-vm/unmap`,
        "POST",
        unmapPayload,
        (response) => {
          setLoading(false);
          setUnmapSuccess(true);
          toastr.success("Success", "UID unmapped successfully");
          resolve(response);
        },
        (error) => {
          const errorMessage = error?.message || "Network error occurred";
          setError(errorMessage);
          toastr.error("Error", errorMessage);
          setLoading(false);
          reject(new Error(errorMessage));
        }
      );
    });
  };

  const handleDashboardMap = (vmid, reason) => {
    if (!uidDetails) return;
    
    setPendingMapData({ vmid, reason });
    setMapConfirmationOpen(true);
  };

  const handleDashboardConfirmedMap = async () => {
    if (!pendingMapData || !uidDetails) return;
    
    setLoading(true);
    setError(null);
    setMapConfirmationOpen(false);
    
    const { vmid, reason } = pendingMapData;
    const currentUid = uidDetails?.uidInfo?.uid || uidDetails?.data?.uidInfo?.uid || uid;
    
    const mapPayload = {
      uids: [currentUid],
      vmIds: [vmid],
    };

    return new Promise((resolve, reject) => {
      props.getDataFromAPI(
        `/partner/api/v2/uid-vm/map`,
        "POST",
        mapPayload,
        (response) => {
          setLoading(false);
          setMapSuccess(true);
          toastr.success("Success", "UID mapped successfully");
          resolve(response);
        },
        (err) => {
          const errorMessage = err?.message || "Network error occurred";
          setError(errorMessage);
          toastr.error("Error", errorMessage);
          setLoading(false);
          reject(new Error(errorMessage));
        }
      );
    });
  };

  const handleDashboardCancelMap = () => {
    setMapConfirmationOpen(false);
    setPendingMapData(null);
  };

  const handleDashboardUidTimeline = () => {
    const uidValue = uidDetails?.uidInfo?.uid || uidDetails?.data?.uidInfo?.uid || uid;
    if (uidValue) {
      navigate(`/home/configuration/uid-dashboard/uid-timeline/${uidValue}`);
    } else {
      toastr.error("Error", "No UID selected");
    }
  };

  const handleDashboardSuccessNavigation = () => {
    setMapSuccess(false);
    setUnmapSuccess(false);
    navigate(-1);
  };

  const resetDashboardSuccessStates = () => {
    setMapSuccess(false);
    setUnmapSuccess(false);
  };

  return (
    <VmidDashboardDetailsComponent
      uidDetails={uidDetails}
      loading={loading}
      error={error}
      navigate={navigate}
      onUnmap={handleDashboardUnmap}
      onMap={handleDashboardMap}
      onConfirmedMap={handleDashboardConfirmedMap}
      onCancelMap={handleDashboardCancelMap}
      onUidSelect={handleDashboardUidSelect}
      onUidListLoad={handleDashboardGetUidList}
      onUidSearch={handleDashboardUidSearch}
      onVmSearch={handleDashboardVmSearch}
      currentMode={currentMode}
      handleUidTimeline={handleDashboardUidTimeline}
      vmList={vmList}
      uidList={uidList}
      selectedVmDetails={selectedVmDetails}
      onVmSelect={handleDashboardVmSelect}
      onSuccessNavigation={handleDashboardSuccessNavigation}
      mapSuccess={mapSuccess}
      unmapSuccess={unmapSuccess}
      resetSuccessStates={resetDashboardSuccessStates}
      mapConfirmationOpen={mapConfirmationOpen}
      pendingMapData={pendingMapData}
      initialVmInfo={initialVmInfo}
      searchLoading={searchLoading}
      searchQuery={searchQuery}
    />
  );
};

export default connect(null, {
  getDataFromAPI,
})(VmidDashboardDetailsContainer);